<template>
    <ItemNotFoundState v-if="notFound" :objectName="$t('gamification.subject')">
        <template #backButton>
            <BackButton :target="{name: 'Subjects'}">{{$t('gamification.subjects')}}</BackButton>
        </template>
    </ItemNotFoundState>
    <v-container v-else-if="!isLoading">
        <v-row>
            <v-col>
                <BackButton :target="{name: 'Subjects'}">{{$t('gamification.subjects')}}</BackButton>
            </v-col>
        </v-row>
        <v-row>
            <v-col>
                <ModuleHeader class="header">
                    <h1 v-if="isNew">{{$t('gamification.add_subject')}}</h1>
                    <h1 v-else> {{$t('gamification.edit_subject')}} </h1>

                    <div class="d-sm-block" style="display: grid">
                        <v-btn
                          v-if="!isNew"
                          @click="confirmDialog = true"
                          color="deep-orange-darken-2" elevation="0" rounded
                          variant="text"
                          class="mr-0 mr-md-8"
                        >
                          {{$t('gamification.archive_subject')}}
                        </v-btn>

                        <SaveButton
                          :disabled="!isFormValid || !getSubject.subjectUsers.length > 0"
                          :is-saving="isSaving"
                          @save="save"
                          :label="$t('gamification.save')"
                        />
                    </div>

                </ModuleHeader>
            </v-col>
        </v-row>

      <confirm-dialog
          :is-open="confirmDialog"
          :title="$t('gamification.archive_subject')"
          :subtitle="$t('gamification.confirm_archive_subject')"
          :confirm-button-text="$t('gamification.archive')"
          @close="confirmDialog = false"
          @confirm-click="deleteSubject(subjectId)" />

      <academics-list
          :initials-selected="getSubject.subjectUsers.map(u => { return {...u, active: true}})"
          ref="academicsList"
          @update="args => replaceUsersFromList(args.users)"
          @close="isAcademicListOpen = false"
          :is-open="isAcademicListOpen" />

        <v-row>
            <v-col>
                <v-form @submit.prevent="save" ref="form" v-model="isFormValid" class="formWidth">
                    <v-row>
                        <v-col>
                            <v-text-field
                              :label="$t('gamification.subject_name')"
                              @change="changeWatch"
                              :rules="[required_rule]"
                              v-model.trim="getSubject.name"
                              clearable
                              :maxlength="100"
                              counter
                              variant="underlined"
                            >
                            </v-text-field>
                        </v-col>
                        <v-col>
                          <div
                              class="w-100 d-flex ga-1"
                          >
                            <v-tooltip max-width="300">
                              <template v-slot:activator="{ props: activatorProps }">
                                <v-icon v-bind="activatorProps" size="small" color="grey-darken-2">info</v-icon>
                              </template>
                              <div>
                                <div class="font-weight-bold">
                                  {{$t('gamification.subject_code')}}
                                </div>
                                <div>
                                  {{$t('gamification.subject_code_info')}}
                                </div>
                              </div>
                            </v-tooltip>
                            <v-text-field
                                :label="$t('gamification.subject_code')"
                                @change="changeWatch"
                                :rules="[required_rule]"
                                v-model.trim="getSubject.code"
                                clearable
                                :maxlength="10"
                                counter
                                variant="underlined"
                            >
                            </v-text-field>
                          </div>
                        </v-col>
                    </v-row>

                  <h2>{{$t('gamification.subject_icon')}}</h2>
                  <v-row class="pt-2">
                    <v-col  sm="3" md="3" lg="2" xl="2" xxl="2" :key="index" v-for="(image,index) in images">
                      <div
                          @click="updateImage(image)"
                          :class="`image-border cursor-pointer ${getSubject.imageId === image.id && 'image-border-active'}`"
                      >
                        <v-img
                            :src="image.imageUrl"
                            width="50px"
                            height="50px"
                        />
                      </div>

                    </v-col>
                    <v-col cols="12">
                      <v-btn
                          v-if="showExpandButton"
                          @click="onlyFirstRow = !onlyFirstRow"
                          color="primary"
                          rounded
                          variant="outlined"
                          >
                        {{onlyFirstRow ? $t('gamification.more_icons') : $t('gamification.hide_icons')}}
                      </v-btn>
                    </v-col>
                  </v-row>


                    <v-row class="m-0 p-0 align-center">
                        <v-col>
                            <h2>{{$t('gamification.academics')}}</h2>
                            <div class="text-grey-darken-2">{{$t('gamification.academics_lead')}}</div>
                        </v-col>
                        <v-col cols="12" md="auto">
                          <v-btn @click="isAcademicListOpen = true" color="primary" prepend-icon="format_list_bulleted" variant="outlined" rounded> {{$t('gamification.edit_selection')}}</v-btn>
                        </v-col>
                    </v-row>

                    <div v-if="getSubject.subjectUsers.length !== 0">
                      <v-data-table
                          class="pt-5"
                          :headers="headers"
                          fixed-header
                          :items="getSubject.subjectUsers"
                          return-object
                      >
                        <template v-slot:item.actions="{item}">
                          <v-btn :disabled="item.uisId === uisId" variant="text" @click="removeUserFromSubject(item)" icon="clear"></v-btn>
                        </template>
                      </v-data-table>
                    </div>
                </v-form>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>

import {mapActions, mapGetters} from "vuex";
import ModuleHeader from "@/components/layout/ModuleHeader";
import BackButton from "@/components/layout/BackButton";
import SaveButton from "@/components/buttons/SaveButton";
import ItemNotFoundState from '@/components/states/ItemNotFoundState.vue';
import AcademicsList from "@/components/gamification/subject/AcademicsList.vue";
import ConfirmDialog from '@/components/gamification/ConfirmDialog.vue'

export default {
    name: "SubjectDetail",

    components: {
      ConfirmDialog,
      AcademicsList,
        SaveButton,
        ModuleHeader,
        BackButton,
        ItemNotFoundState
    },

    data() {
        return {
            required_rule: value => !!value || this.$t('gamification.required'),
            isSaving: false,
            isFormValid: false,
            newUser: null,
            isLoading: true,
            notFound: false,
            isAcademicListOpen: false,
            headers: [
              { key: 'fullName', title: this.$t('gamification.fullname')},
              { key: 'login', title: 'xlogin'},
              { key: 'uisId', title: 'UIS Id'},
              { key: 'actions', title: this.$t('gamification.action'), align: 'end', sortable: false}
            ],
            confirmDialog: false,
            onlyFirstRow: true,
        }
    },

    async created() {
        await this.loadData()
        if(this.isNew){
          await this.appendLoggedAdmin()
        }
        this.isLoading = false
    },

  watch: {
        subjectId() {
            this.loadData();
        }
    },
    computed: {
        ...mapGetters('Subjects', ['getSubjects', 'getSubjectById','getSubject','getImages']),
        ...mapGetters('user',['username','uisId']),

        subjectId() {
            const urlId = this.$route.params.subjectId;
            return urlId === "new" ? null : Number(urlId);
        },

        isNew() {
            return this.subjectId === null;
        },
      imagesPerRow() {
        if (this.$vuetify.display.xlAndUp) return 8
        if (this.$vuetify.display.lgAndUp) return 6
        if (this.$vuetify.display.smAndUp || this.$vuetify.display.mdAndUp) return 4
        else return 2
      },
      showExpandButton(){
          return this.getImages.length > this.imagesPerRow
      },
      images() {
          if(this.onlyFirstRow && this.getImages.length > this.imagesPerRow){
            return this.getImages.slice(0, this.imagesPerRow)
          } else {
            return this.getImages
          }
      }
    },

    methods: {
        ...mapActions('leavePrevent', ['madeUnsavedChanges', 'didSaveChanges']),
        ...mapActions('Subjects', ['fetchAllSubjects', 'fetchSubjectById', 'createSubject', 'updateSubject', 'deleteSubjectById', 'setSubject', 'clearSubject','fetchImages']),
        ...mapActions('user', ['fetchByLoginOrId']),

        async loadData() {
          await this.fetchImages()
            if (!this.isNew) {
                try {
                    const s = await this.fetchSubjectById(this.subjectId);
                    this.setSubject({ name: s.name, code: s.code, id: s.id, subjectUsers: s.admins, imageId: s.imageId })
                    this.moveImageToFirst(s.imageId)
                } catch (e) {
                    if (e.statusCode === 404) {
                        this.notFound = true;
                    } else {
                        throw e;
                    }
                }
            } else {
              this.clearSubject()
              this.getSubject.imageId = this.getImages[0]?.id ?? null
            }
        },
        replaceUsersFromList(users){
          this.getSubject.subjectUsers = users
        },
        changeWatch() {
            this.madeUnsavedChanges();
        },

        async deleteSubject(id) {
          await this.deleteSubjectById(id)
          this.$router.push({name: 'Subjects'})
        },

        removeUserFromSubject(user) {
            this.getSubject.subjectUsers = this.getSubject.subjectUsers.filter(u => u.uisId !== user.uisId)
        },

        async appendLoggedAdmin(){
          const response = await this.fetchByLoginOrId({xlogin: this.username})
          this.getSubject.subjectUsers.push({...response.data.content})
        },

        updateImage(image){
          this.getSubject.imageId = image.id
        },

        moveImageToFirst(id){
          const selectedImageIndex = this.getImages.findIndex(i => i.id === id)
          if (selectedImageIndex > -1) {
            const [item] = this.getImages.splice(selectedImageIndex, 1)
            this.getImages.unshift(item)
          }
        },

        async save() {
                this.isSaving = true;
                if (this.isNew) {
                    this.getSubject.id = await this.createSubject({item: {
                        name: this.getSubject.name,
                        code: this.getSubject.code,
                        imageId: this.getSubject.imageId,
                        uisIds: this.getSubject.subjectUsers.map(u => u.uisId)
                      }});
                } else {
                    await this.updateSubject({item: {
                        id: this.getSubject.id,
                        name: this.getSubject.name,
                        code: this.getSubject.code,
                        imageId: this.getSubject.imageId,
                        uisIds: this.getSubject.subjectUsers.map(u => u.uisId)
                      }});
                }

                this.didSaveChanges();

                if (this.isNew) {
                    await this.$router.push({name: 'Subjects'});
                }

                this.isSaving = false;
        }
    }

}
</script>

<style scoped>
.image-border {
  border: 1px solid #CCCCCC;
  border-radius: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 130px;
  height: 80px;
}

.image-border-active {
  border: 2px solid #78BE13;
}

thead {
    border-top: none;
}

th, tr, td {
    border: none !important;
    padding: 0 !important;
}

th {
    font-size: 1rem !important;
}

</style>